/**
 * /auth/signup — sign-up screen. Phase 3b of ottotester#65.
 *
 * Calls Better Auth's `signUp.email` which creates the user and triggers
 * the verification email (per Phase 3a's `sendVerificationEmail`
 * callback in betterAuth.ts). On success, lands on a "check your inbox"
 * confirmation card; user clicks the email link to verify, which lands
 * on /auth/verify-email.
 *
 * When this installation can't send email, verification is off and
 * signing up signs the person in. There is no inbox card for an email
 * that never comes: they go straight to the invitation they came from,
 * or to first-time setup.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, MailCheck } from 'lucide-react'

import { Button, Input, Label, PasswordInput } from '@/components/ui'
import { authClient } from '@/auth/authClient'
import { AuthLayout } from '@/auth/AuthLayout'
import { OAuthRow, type OAuthProvider } from '@/auth/OAuthRow'
import { Divider } from '@/auth/Divider'
import { usePublicSettings } from '@/hooks/usePublicSettings'
import { useIdentity } from '@/hooks/useIdentity'
import type { PublicSettings } from '@/lib/api'

/** Resend-verification block constants.
 *
 * Two independent timers fire on the success path:
 *   - `RESEND_COOLDOWN_SECONDS` — locks the button so a customer doesn't
 *     drum on it; runs to zero, then the button re-enables.
 *   - `RESEND_STATUS_CLEAR_MS` — drops the "Sent — check your inbox"
 *     copy, leaving just the disabled cooldown button. Mockup state 6.
 *
 * The cooldown is the source of truth for clickability; the status copy
 * is informational and short-lived. The error path uses neither — a
 * failed send re-enables the button immediately so retry is one click,
 * and the error copy lingers until the next click resets it. The
 * defense-in-depth gate against abuse is Better-Auth's built-in
 * /send-verification-email rate limiter (3 req / 60s window). */
const RESEND_COOLDOWN_SECONDS = 30
const RESEND_STATUS_CLEAR_MS = 5000

type ResendStatus =
  | { kind: 'idle' }
  | { kind: 'success' }
  | { kind: 'error' }

/** sessionStorage key for the post-verify-email redirect target.
 * Signup writes this; VerifyEmail reads + clears it. Used for the
 * invitation flow: clicking an invite link → /auth/signup?returnTo=
 * → after email verification, land back on /accept-invitation/<id>
 * instead of the default needsOnboarding bounce. */
const POST_VERIFY_RETURN_TO_KEY = 'ottotester.postVerifyReturnTo'

/** Allow only same-origin pathnames as returnTo to prevent open
 * redirects. Anything that smells like an absolute URL or a
 * protocol-relative path is rejected. */
function safeReturnTo(value: string | null): string | null {
  if (!value) return null
  if (!value.startsWith('/')) return null
  if (value.startsWith('//')) return null
  return value
}

export function Signup() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { refreshIdentity } = useIdentity()
  const returnTo = safeReturnTo(searchParams.get('returnTo'))
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailSubmitting, setEmailSubmitting] = useState(false)
  const [oauthSubmitting, setOauthSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  // The terms and privacy pages come from the server, each only when this
  // installation set it. With neither set there is no box to tick. Until
  // the server answers (or when it fails) nothing can be submitted, so a
  // failed request never reads as "no agreement needed".
  const publicSettings = usePublicSettings()
  const legalPages = publicSettings.status === 'ready' ? publicSettings.settings : null
  const asksForAgreement = Boolean(legalPages?.termsUrl || legalPages?.privacyUrl)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const canProceed = legalPages !== null && (!asksForAgreement || acceptedTerms)
  const emailOff = legalPages !== null && !legalPages.email.enabled

  /** Stash the returnTo so the post-verify screen can pick it up.
   * sessionStorage (not localStorage) so it dies with the tab —
   * this is a one-shot redirect target, not durable state. */
  function persistReturnTo() {
    if (returnTo) {
      try {
        sessionStorage.setItem(POST_VERIFY_RETURN_TO_KEY, returnTo)
      } catch {
        // Storage unavailable (Safari private mode, quota); fall
        // through silently — the user lands on /onboarding instead
        // of the invite page, but invitation discovery on Onboarding
        // recovers them.
      }
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // Defense in depth: the submit button is disabled until consent, but
    // never POST consent we didn't actually get (a bypassed disabled button).
    if (!canProceed) return
    setError(null)
    setEmailSubmitting(true)
    // With email off there is no verify step to hand the returnTo to.
    if (!emailOff) persistReturnTo()
    const { error } = await authClient.signUp.email({ email, password, name })
    if (error) {
      setEmailSubmitting(false)
      setError(error.message ?? 'Sign-up failed')
      return
    }
    if (emailOff) {
      // Signed in already. Refresh the identity snapshot first, as Login
      // does, so the next route sees the session.
      await refreshIdentity()
      navigate(returnTo ?? '/onboarding', { replace: true })
      return
    }
    setEmailSubmitting(false)
    setSubmittedEmail(email)
  }

  async function handleOAuth(provider: OAuthProvider) {
    setError(null)
    setOauthSubmitting(true)
    persistReturnTo()
    // OAuth signups never go through email verification, so the
    // callback URL itself can carry the returnTo — Better Auth
    // forwards through it after the social-login round-trip.
    const callbackURL = returnTo ?? '/'
    // `errorCallbackURL` — see Login.tsx for the same forward-compat
    // note; the actual redirect-on-error is wired server-side via
    // `onAPIError.errorURL` in `infrastructure/auth/betterAuth.ts`.
    const { error } = await authClient.signIn.social({
      provider,
      callbackURL,
      errorCallbackURL: '/auth/error',
    })
    if (error) {
      setOauthSubmitting(false)
      setError(error.message ?? `${provider} sign-up failed`)
    }
  }

  // Post-submit confirmation card — replaces the form so the user
  // doesn't accidentally re-submit. They can click the verification
  // link in the email they just received, OR use the resend block
  // below if it never arrived (SES misconfig, spam folder, typo).
  if (submittedEmail) {
    return (
      <ResendableInboxCard email={submittedEmail} sender={legalPages?.email.sender} />
    )
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Set up your account to get started."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/auth/login" className="text-primary hover:underline font-medium">
            Sign in
          </Link>
        </>
      }
    >
      {publicSettings.status === 'error' && (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>We couldn't load this sign-up page. Check your connection and try again.</p>
          <Button type="button" variant="outline" size="sm" onClick={publicSettings.retry}>
            Try again
          </Button>
        </div>
      )}

      {legalPages && asksForAgreement && (
        <AgreementCheckbox
          termsUrl={legalPages.termsUrl}
          privacyUrl={legalPages.privacyUrl}
          checked={acceptedTerms}
          onChange={setAcceptedTerms}
        />
      )}

      <OAuthRow onSelect={handleOAuth} disabled={oauthSubmitting || !canProceed} />

      <Divider label="or" />

      <form onSubmit={handleSubmit} className="space-y-3" aria-label="Sign up form">
        <div className="space-y-1.5">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            type="text"
            autoComplete="name"
            autoFocus
            required
            placeholder="How should we address you?"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={emailSubmitting || oauthSubmitting || !canProceed}
          className="w-full"
        >
          {emailSubmitting ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  )
}

/**
 * The "I agree to ..." box, naming only the pages this installation set.
 * The caller renders it only when at least one is set.
 */
function AgreementCheckbox({
  termsUrl,
  privacyUrl,
  checked,
  onChange,
}: {
  termsUrl?: string
  privacyUrl?: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  const linkClass = 'text-primary hover:underline'
  const names = [termsUrl && 'Terms of Service', privacyUrl && 'Privacy Policy'].filter(Boolean)
  return (
    <label className="flex items-start gap-2 text-xs text-muted-foreground">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-3.5 w-3.5"
        aria-label={`I agree to the ${names.join(' and ')}`}
      />
      <span>
        I agree to the{' '}
        {termsUrl && (
          <a href={termsUrl} target="_blank" rel="noreferrer" className={linkClass}>
            Terms of Service
          </a>
        )}
        {termsUrl && privacyUrl && ' and '}
        {privacyUrl && (
          <a href={privacyUrl} target="_blank" rel="noreferrer" className={linkClass}>
            Privacy Policy
          </a>
        )}
        .
      </span>
    </label>
  )
}

/**
 * Post-signup "Check your inbox" card with the resend-verification
 * affordance. State machine:
 *   idle → sending → cooldown (30s) → idle    on success
 *   idle → sending → idle                     on error
 *
 * Cooldown timer (1s tick) decrements `secondsLeft` from 30 to 0 and
 * is the source of truth for whether the button is clickable. A
 * separate 5s status-clear timer drops the success copy independently
 * of the cooldown — mockup state 6 is the resulting cooldown-only
 * sub-state. The error path uses neither: a failed send re-enables the
 * button immediately and the error copy lingers until the next click.
 *
 * Refs hold the interval / timeout ids so unmount can clean them up
 * even if the timers haven't fired yet.
 */
function ResendableInboxCard({
  email,
  sender,
}: {
  email: string
  /** Who the email comes from, as the server really sends it. Absent
   * means the server named no sender, so the note is left out rather
   * than guessed. */
  sender?: NonNullable<PublicSettings['email']['sender']>
}) {
  const [sending, setSending] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [status, setStatus] = useState<ResendStatus>({ kind: 'idle' })

  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const statusClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current !== null) clearInterval(cooldownIntervalRef.current)
      if (statusClearTimeoutRef.current !== null) clearTimeout(statusClearTimeoutRef.current)
    }
  }, [])

  function startCooldown() {
    setSecondsLeft(RESEND_COOLDOWN_SECONDS)
    if (cooldownIntervalRef.current !== null) clearInterval(cooldownIntervalRef.current)
    cooldownIntervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (cooldownIntervalRef.current !== null) {
            clearInterval(cooldownIntervalRef.current)
            cooldownIntervalRef.current = null
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  function scheduleSuccessCopyClear() {
    if (statusClearTimeoutRef.current !== null) clearTimeout(statusClearTimeoutRef.current)
    statusClearTimeoutRef.current = setTimeout(() => {
      setStatus({ kind: 'idle' })
      statusClearTimeoutRef.current = null
    }, RESEND_STATUS_CLEAR_MS)
  }

  async function handleResend() {
    // Clear any lingering error copy from a previous failed attempt
    // before kicking off the new request (mockup: clicking again
    // dismisses the error). Cooldown gates the click, so a click
    // implies cooldown isn't active.
    setStatus({ kind: 'idle' })
    setSending(true)
    const { error } = await authClient.sendVerificationEmail({ email })
    setSending(false)
    if (error) {
      // Error path: leave the button enabled so retry is one click;
      // surface the error copy until the next click resets it.
      setStatus({ kind: 'error' })
      return
    }
    setStatus({ kind: 'success' })
    startCooldown()
    scheduleSuccessCopyClear()
  }

  const cooldownActive = secondsLeft > 0
  const buttonDisabled = sending || cooldownActive

  return (
    <AuthLayout title="Check your inbox" subtitle={`We sent a verification link to ${email}.`}>
      <div className="flex flex-col items-center gap-4 py-2">
        <MailCheck className="h-10 w-10 text-primary" aria-hidden="true" />
        <p className="text-sm text-muted-foreground text-center">
          Click the link in the email to verify your address. The link expires in 24 hours.
        </p>
        {/* Sender hint doubles as a spam-folder nudge and an allowlist aid —
            transactional "verify" mail is the classic thing that gets filtered.
            The sender comes from the server, the same one the email uses. */}
        {sender && (
          <p className="text-xs text-muted-foreground text-center">
            Don't see it? Check your spam or junk folder — it comes from{' '}
            <span className="font-medium text-foreground">
              {sender.name ? `${sender.name} (${sender.address})` : sender.address}
            </span>
            .
          </p>
        )}

        <div className="w-full border-t pt-4 space-y-2">
          <p className="text-xs text-muted-foreground text-center">Didn't get it?</p>
          <Button
            type="button"
            variant="outline"
            onClick={handleResend}
            disabled={buttonDisabled}
            aria-disabled={buttonDisabled}
            className="w-full border-primary/40 text-primary hover:bg-primary/5 hover:text-primary"
          >
            {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
            {sending
              ? 'Sending…'
              : cooldownActive
                ? `Resend in ${secondsLeft}s`
                : 'Resend verification email'}
          </Button>
          <div
            role="status"
            aria-live="polite"
            className="min-h-[1rem] text-xs text-center"
          >
            {status.kind === 'success' && (
              <span className="text-success">Sent — check your inbox</span>
            )}
            {status.kind === 'error' && (
              <span className="text-destructive">Couldn't send. Try again.</span>
            )}
          </div>
        </div>

        <Link to="/auth/login" className="text-sm text-primary hover:underline">
          Back to sign in
        </Link>
      </div>
    </AuthLayout>
  )
}
